import { supabase } from './supabase'

export interface QuizSession {
  id: string
  sessionId: string
  quizType: 'patient' | 'caregiver'
  startedAt: number
  endedAt?: number
  status: 'in_progress' | 'completed' | 'abandoned'
  currentPage: number
  totalPagesVisited: number
  abandonedAtPage?: number
  userAnswers?: Record<string, any>
  createdAt: string
  updatedAt: string
}

export async function saveQuizSessionToSupabase(session: any) {
  try {
    if (!supabase) {
      console.warn('Supabase not initialized')
      return null
    }

    const { data, error } = await supabase
      .from('quiz_sessions')
      .upsert(
        {
          session_id: session.sessionId,
          quiz_type: session.quizType,
          started_at: new Date(session.startedAt).toISOString(),
          ended_at: session.endedAt ? new Date(session.endedAt).toISOString() : null,
          status: session.status,
          current_page: session.currentPage,
          total_pages_visited: session.totalPagesVisited,
          abandoned_at_page: session.abandonedAtPage || null,
          user_answers: session.userAnswers || {},
        },
        { onConflict: 'session_id' }
      )
      .select()

    if (error) {
      console.error('Error saving quiz session:', error)
      return null
    }

    return data?.[0]
  } catch (error) {
    console.error('Failed to save quiz session to Supabase:', error)
    return null
  }
}

export async function fetchQuizSessions(options?: {
  quizType?: 'patient' | 'caregiver'
  status?: 'in_progress' | 'completed' | 'abandoned'
  limit?: number
  offset?: number
}) {
  try {
    if (!supabase) {
      console.warn('Supabase not initialized')
      return []
    }

    let query = supabase
      .from('quiz_sessions')
      .select('*')
      .order('started_at', { ascending: false })

    if (options?.quizType) {
      query = query.eq('quiz_type', options.quizType)
    }

    if (options?.status) {
      query = query.eq('status', options.status)
    }

    if (options?.limit) {
      query = query.limit(options.limit)
    }

    if (options?.offset) {
      query = query.range(options.offset, options.offset + (options.limit || 50) - 1)
    }

    const { data, error } = await query

    if (error) {
      console.error('Error fetching quiz sessions:', error)
      return []
    }

    return (data || []).map((session: any) => ({
      sessionId: session.session_id,
      quizType: session.quiz_type,
      startedAt: new Date(session.started_at).getTime(),
      endedAt: session.ended_at ? new Date(session.ended_at).getTime() : undefined,
      status: session.status,
      currentPage: session.current_page,
      totalPagesVisited: session.total_pages_visited,
      abandonedAtPage: session.abandoned_at_page || undefined,
      userAnswers: session.user_answers || {},
    }))
  } catch (error) {
    console.error('Failed to fetch quiz sessions from Supabase:', error)
    return []
  }
}

export async function fetchQuizSessionById(sessionId: string) {
  try {
    if (!supabase) {
      console.warn('Supabase not initialized')
      return null
    }

    const { data, error } = await supabase
      .from('quiz_sessions')
      .select('*')
      .eq('session_id', sessionId)
      .single()

    if (error) {
      console.error('Error fetching quiz session:', error)
      return null
    }

    return {
      sessionId: data.session_id,
      quizType: data.quiz_type,
      startedAt: new Date(data.started_at).getTime(),
      endedAt: data.ended_at ? new Date(data.ended_at).getTime() : undefined,
      status: data.status,
      currentPage: data.current_page,
      totalPagesVisited: data.total_pages_visited,
      abandonedAtPage: data.abandoned_at_page || undefined,
      userAnswers: data.user_answers || {},
    }
  } catch (error) {
    console.error('Failed to fetch quiz session:', error)
    return null
  }
}

export async function getQuizAnalytics(daysBack: number = 30) {
  try {
    if (!supabase) {
      console.warn('Supabase not initialized')
      return null
    }

    const startDate = new Date()
    startDate.setDate(startDate.getDate() - daysBack)

    const { data, error } = await supabase
      .from('quiz_sessions')
      .select('*')
      .gte('started_at', startDate.toISOString())

    if (error) {
      console.error('Error fetching analytics:', error)
      return null
    }

    const sessions = data || []
    const completed = sessions.filter((s: any) => s.status === 'completed').length
    const abandoned = sessions.filter((s: any) => s.status === 'abandoned').length
    const inProgress = sessions.filter((s: any) => s.status === 'in_progress').length

    return {
      totalSessions: sessions.length,
      completedSessions: completed,
      abandonedSessions: abandoned,
      inProgressSessions: inProgress,
      completionRate: sessions.length > 0 ? (completed / sessions.length) * 100 : 0,
      byQuizType: {
        patient: sessions.filter((s: any) => s.quiz_type === 'patient').length,
        caregiver: sessions.filter((s: any) => s.quiz_type === 'caregiver').length,
      },
    }
  } catch (error) {
    console.error('Failed to get quiz analytics:', error)
    return null
  }
}
